import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import onboardingService from "../../services/onboardingService";
import { apiService } from "../../services/apiService";
import {
  getPaymentStatus,
  initializePlan,
} from "../../services/paymentsService";
import { useAppDispatch } from "../../store/store";
import { completeOnboarding as completeOnboardingAction } from "../../store/slices/onboardingSlice";
import { loadOnboardingData, clearOnboardingData } from "../../utils/storage";
import supabase from "../../lib/supabaseClient";
import styles from "./OnboardingComplete.module.css";

// Plan configuration
const PLAN_DETAILS: Record<
  string,
  { name: string; price: string; features: string[] }
> = {
  free: {
    name: "Free Plan",
    price: "Free",
    features: ["1 send per week", "Basic integrations", "Email support"],
  },
  basic: {
    name: "Basic Plan",
    price: "$50/month",
    features: ["Unlimited sends", "Automations", "Email support"],
  },
  pro: {
    name: "Pro Plan",
    price: "$75/month",
    features: [
      "Unlimited sends",
      "Automations",
      "Data room",
      "Deck storage",
      "Priority support",
    ],
  },
  enterprise: {
    name: "Enterprise Plan",
    price: "Custom",
    features: ["Everything in Pro", "Dedicated support", "Custom features"],
  },
};

/**
 * Final step of onboarding: confirms payment, persists data,
 * updates user metadata, and navigates to dashboard.
 */
const OnboardingComplete: React.FC = () => {
  const { user, checkOnboardingStatus, markOnboardingComplete } = useAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>("basic");

  // Use ref to prevent multiple executions in React.StrictMode
  const hasRunRef = useRef<boolean>(false);

  // Apply dark theme
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.classList.add("onboarding-dark");
      document.body.classList.add("onboarding-dark");
      return () => {
        document.documentElement.classList.remove("onboarding-dark");
        document.body.classList.remove("onboarding-dark");
      };
    }
  }, []);

  useEffect(() => {
    const finalize = async () => {
      // Prevent multiple executions in React.StrictMode
      if (hasRunRef.current) {
        console.log(
          "OnboardingComplete: Skipping duplicate execution (StrictMode)",
        );
        return;
      }
      hasRunRef.current = true;

      try {
        // 1. Ensure user is authenticated
        console.log("OnboardingComplete: user", user);
        if (!user) {
          navigate("/login", { replace: true });
          return;
        }

        // 2. Extract payment/session ID and plan from URL (optional)
        const params = new URLSearchParams(window.location.search);
        const sessionId =
          params.get("payment_intent") || params.get("session_id");
        const planFromUrl = params.get("plan");
        const isPromo = params.get("promo") === "true";

        // Check if user is already onboarded
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("onboarding_complete, subscription_tier")
          .eq("id", user.id)
          .single();

        const isAlreadyOnboarded = userData?.onboarding_complete === true;

        console.log(
          "OnboardingComplete: isAlreadyOnboarded",
          isAlreadyOnboarded,
        );

        // If already onboarded, handle as a subscription upgrade
        if (isAlreadyOnboarded) {
          console.log(
            "OnboardingComplete: User already onboarded, handling as upgrade",
          );

          // Verify payment if session ID exists
          if (sessionId) {
            console.log(
              "Verifying payment status for upgrade session:",
              sessionId,
            );
            setPaymentStatus("verifying");
            try {
              const statusResp = await getPaymentStatus(sessionId);
              console.log("Payment status response:", statusResp);

              setPaymentStatus(statusResp.status);

              if (
                statusResp.status === "paid" ||
                statusResp.status === "complete" ||
                statusResp.status === "active"
              ) {
                console.log("Payment verified successfully for upgrade");
                // Update payments record with session ID
                try {
                  await supabase
                    .from("payments")
                    .update({ session_id: sessionId })
                    .eq("user_id", user.id);
                } catch (updateErr) {
                  console.warn("Failed to update payments record:", updateErr);
                }
              }
            } catch (paymentErr) {
              console.warn("Failed to verify payment:", paymentErr);
              setPaymentStatus("error");
            }
          }

          // Redirect to dashboard with success message
          setLoading(false);
          setTimeout(() => {
            navigate("/?upgraded=true", { replace: true });
          }, 2000);
          return;
        }

        // Load onboarding data from localStorage, with fallback to users table
        const onboardingData = loadOnboardingData();
        console.log(
          "OnboardingComplete: onboardingData from localStorage",
          onboardingData,
        );

        // Extract plan from URL, onboarding data, or default to basic
        const plan = planFromUrl || onboardingData?.payment_plan || "basic";
        setSelectedPlan(plan);

        // Do not call users table directly; rely on local storage or server flow

        if (!onboardingData) {
          setError(
            "Onboarding data not found. Please restart the onboarding process.",
          );
          setLoading(false);
          return;
        }

        // 3. Verify payment and update plan (uses same reliable endpoint as upgrade flow)
        // Skip payment verification for promo users — redemption already happened in PaymentPlans
        if (isPromo) {
          console.log("OnboardingComplete: Promo flow, skipping payment verification");
          setPaymentStatus("promo");
          localStorage.removeItem("promo_code");
        } else if (sessionId) {
          console.log("Verifying payment and updating plan for session:", sessionId);
          setPaymentStatus("verifying");
          try {
            // Primary path: verifyAndUpdatePlan reads plan from Stripe customer metadata
            const verifyResp = await apiService.post("/payments/verify-and-update", { sessionId });
            if (verifyResp.data?.success) {
              setPaymentStatus("paid");
              if (verifyResp.data.plan) {
                setSelectedPlan(verifyResp.data.plan);
              }
              console.log(`Plan verified and updated: ${verifyResp.data.plan}`);
            } else {
              // Fallback: use initializePlan with plan from localStorage/URL
              console.warn("verifyAndUpdatePlan returned unsuccessful, falling back to initializePlan");
              const fallbackPlan = planFromUrl || onboardingData?.payment_plan || "basic";
              try {
                await initializePlan(fallbackPlan as "free" | "basic" | "pro" | "enterprise");
                console.log(`Initialized plan via fallback: ${fallbackPlan}`);
              } catch (initErr) {
                console.warn("initializePlan fallback also failed:", initErr);
              }
              setPaymentStatus("paid");
            }
          } catch (verifyErr) {
            console.warn("Failed to verify payment, trying initializePlan fallback:", verifyErr);
            // Fallback: use initializePlan with plan from localStorage/URL
            const fallbackPlan = planFromUrl || onboardingData?.payment_plan || "basic";
            try {
              await initializePlan(fallbackPlan as "free" | "basic" | "pro" | "enterprise");
              console.log(`Initialized plan via fallback: ${fallbackPlan}`);
              setPaymentStatus("paid");
            } catch (initErr) {
              console.warn("initializePlan fallback also failed:", initErr);
              setPaymentStatus("error");
            }
            // Payment verification failure should not block onboarding completion
          }
        } else {
          console.log(
            "No session ID found, skipping payment verification (free plan)",
          );
          setPaymentStatus("free");
        }

        // 4. Complete the onboarding process
        try {
          // Call backend to atomically finalize onboarding (idempotent)
          try {
            await apiService.completeOnboarding({ onboarding: onboardingData });
          } catch (serverErr) {
            console.warn(
              "Server-side onboarding completion failed, falling back to client path:",
              serverErr,
            );
            // Fallback to legacy client-side completion
            await onboardingService.completeOnboarding(user.id, onboardingData);
          }

          // 5. Update Supabase Auth user metadata with completion flags only
          await supabase.auth.updateUser({
            data: {
              onboarded: true,
              onboarding_completed_at: new Date().toISOString(),
            },
          });

          // 6. Redux update
          dispatch(completeOnboardingAction());

          console.log("Onboarding completed successfully");

          // 7. Immediately mark onboarding complete locally to avoid route loops
          markOnboardingComplete();

          // 8. Proactively refresh onboarding status in AuthContext before redirect
          try {
            await checkOnboardingStatus();
          } catch (refreshErr) {
            console.warn(
              "OnboardingComplete: onboarding status refresh failed",
              refreshErr,
            );
          }

          // 9. Clear localStorage (now safe since context has refreshed)
          clearOnboardingData();

          // 10. Redirect to dashboard
          navigate("/", { replace: true });
        } catch (completionErr) {
          console.error("Failed to complete onboarding:", completionErr);
          setError("Failed to complete onboarding. Please try again.");
          setLoading(false);
          return;
        }
      } catch (err: any) {
        console.error("Onboarding finalization error:", err);
        setError(
          err.message ||
            "An unexpected error occurred. Please try again or contact support.",
        );
      } finally {
        setLoading(false);
      }
    };

    finalize();
  }, [user, navigate, dispatch]);

  const isPromoFlow = paymentStatus === "promo";
  const basePlanDetails = PLAN_DETAILS[selectedPlan] || PLAN_DETAILS.basic;
  const planDetails = isPromoFlow
    ? { ...basePlanDetails, price: "Free for 6 months" }
    : basePlanDetails;

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.iconWrapper + " " + styles.iconLoading}>
            <div className={styles.spinner}></div>
          </div>
          <h1 className={`${styles.heading} ${styles.loadingHeading}`}>
            {paymentStatus === "verifying"
              ? "Verifying Payment..."
              : paymentStatus === "promo"
              ? "Activating Your Pro Access..."
              : "Setting Up Your Account..."}
          </h1>
          <p className={styles.message}>
            Please wait while we complete your onboarding.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {error ? (
          <>
            <div className={styles.iconWrapper + " " + styles.iconError}>✕</div>
            <h1 className={`${styles.heading} ${styles.errorHeading}`}>
              Onboarding Error
            </h1>
            <div className={styles.errorDetails}>
              <div className={styles.errorTitle}>What happened?</div>
              <div className={styles.errorText}>{error}</div>
            </div>
            <div className={styles.buttonGroup}>
              <button
                onClick={() => window.location.reload()}
                className={`${styles.button} ${styles.buttonPrimary}`}
              >
                Retry
              </button>
              <button
                onClick={() => navigate("/onboarding", { replace: true })}
                className={`${styles.button} ${styles.buttonSecondary}`}
              >
                Restart Onboarding
              </button>
            </div>
            <p className={styles.helpText}>
              Need help?{" "}
              <a
                href="mailto:support@sendupdates.ai"
                className={styles.helpLink}
              >
                Contact Support
              </a>
            </p>
          </>
        ) : (
          <>
            <div className={styles.iconWrapper + " " + styles.iconSuccess}>
              ✓
            </div>
            <h1 className={`${styles.heading} ${styles.successHeading}`}>
              Welcome Aboard! 🎉
            </h1>

            {paymentStatus === "promo" && (
              <span
                className={`${styles.statusBadge} ${styles.statusSuccess}`}
              >
                Promo Code Applied
              </span>
            )}
            {paymentStatus &&
              paymentStatus !== "free" &&
              paymentStatus !== "error" &&
              paymentStatus !== "promo" && (
                <span
                  className={`${styles.statusBadge} ${styles.statusSuccess}`}
                >
                  Payment Confirmed
                </span>
              )}

            <p className={styles.message}>
              Your onboarding is complete! You&apos;re all set to start using
              SendUpdates AI.
            </p>

            <div className={styles.planDetails}>
              <div className={styles.planTitle}>Your Plan</div>
              <div className={styles.planName}>{planDetails.name}</div>
              <div className={styles.planPrice}>{planDetails.price}</div>
              <ul className={styles.planFeatures}>
                {planDetails.features.map((feature, index) => (
                  <li key={index} className={styles.planFeature}>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <p className={styles.message}>
              Redirecting you to your dashboard...
            </p>

            <div className={styles.buttonGroup}>
              <button
                onClick={() => navigate("/", { replace: true })}
                className={`${styles.button} ${styles.buttonPrimary}`}
              >
                Go to Dashboard
              </button>
            </div>

            <p className={styles.helpText}>
              Questions?{" "}
              <a
                href="mailto:support@sendupdates.ai"
                className={styles.helpLink}
              >
                We&apos;re here to help
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardingComplete;
